import React, { useState, useEffect } from 'react'
import { Alert, AlertIcon, AlertDescription, CloseButton, Box, Link } from '@chakra-ui/react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useAppSelector } from '../app/typedReduxHooks'
import { RootState } from '../app/store'

const TrialExpirationBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [isClosedByUser, setIsClosedByUser] = useState(false)
  const location = useLocation()

  const organization = useAppSelector((state: RootState) => state.organization?.data)
  const subscription = useAppSelector((state: RootState) => state.billing?.subscription.data)
  const organizationAdmin = useAppSelector((state: RootState) => state.organizationAdmin.profile)

  const isOwner = Boolean(organizationAdmin?.isOwner)
  const hasPaymentMethod = Boolean(organization?.hasPaymentMethod)
  const trialEnd = subscription?.trialEnd
  const isOnUsersPage =
    location.pathname.includes('/settings') && location.search.includes('tab=users')

  useEffect(() => {
    if (isClosedByUser || !trialEnd || hasPaymentMethod) {
      setIsVisible(false)
      return
    }

    const trialEndDate = new Date(trialEnd)
    const now = new Date()
    const daysUntilExpiry = Math.ceil(
      (trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Show banner if trial expires within 5 days and no payment method is set
    if (daysUntilExpiry <= 5 && daysUntilExpiry > 0) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [trialEnd, hasPaymentMethod, isClosedByUser])

  const handleClose = () => {
    setIsClosedByUser(true)
    setIsVisible(false)
  }

  const getDaysText = (): string => {
    if (!trialEnd) return ''
    const trialEndDate = new Date(trialEnd)
    const now = new Date()
    const daysUntilExpiry = Math.ceil(
      (trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (daysUntilExpiry === 1) return '1 day'
    return `${daysUntilExpiry} days`
  }

  if (!isVisible) {
    return null
  }

  return (
    <Box position="relative" zIndex="banner">
      <Alert status="warning" variant="solid">
        <AlertIcon />
        <Box flex="1">
          <AlertDescription>
            Your trial period expires in {getDaysText()}.{' '}
            {isOwner ? (
              <>
                <Link as={RouterLink} to="/settings?tab=subscription" textDecoration="underline">
                  Set up a payment method
                </Link>{' '}
                to maintain uninterrupted access to RxPad.
              </>
            ) : (
              <>
                Please contact your organisation administrator to configure billing settings.{' '}
                {isOnUsersPage ? (
                  'You can identify the account owner in the team member list below.'
                ) : (
                  <>
                    <Link as={RouterLink} to="/settings?tab=users" textDecoration="underline">
                      View team members
                    </Link>{' '}
                    to identify the account owner.
                  </>
                )}
              </>
            )}
          </AlertDescription>
        </Box>
        <CloseButton onClick={handleClose} />
      </Alert>
    </Box>
  )
}

export default TrialExpirationBanner
